import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { AuthService } from '../auth/auth.service';

/**
 * Preloading strategy that loads lazy modules in the background
 * based on the user's roles.
 *
 * Only routes whose `data.requiredRoles` overlap with the user's
 * roles are preloaded — all others are skipped.
 * This keeps initial bundle small while ensuring menu clicks feel instant.
 */
@Injectable({ providedIn: 'root' })
export class RolePreloadingStrategy implements PreloadingStrategy {
  private userRoles: string[] | null = null;

  constructor(private authService: AuthService) {}

  preload(route: Route, loadFn: () => Observable<any>): Observable<any> {
    const requiredRoles: string[] | undefined = route.data?.['requiredRoles'];

    // Always preload routes without role requirements (e.g. profile, 404)
    if (!requiredRoles || requiredRoles.length === 0) {
      return loadFn();
    }

    // Resolve user roles once, then decide
    return this.getUserRoles().pipe(
      switchMap((roles) => {
        const hasAccess = requiredRoles.some((r) =>
          roles.map((ur) => ur.toLowerCase()).includes(r.toLowerCase()),
        );

        if (hasAccess) {
          return loadFn();
        }

        return EMPTY;
      }),
      catchError(() => EMPTY),
    );
  }

  private getUserRoles(): Observable<string[]> {
    if (this.userRoles !== null) {
      return of(this.userRoles);
    }

    return from(this.authService.getUserRoles()).pipe(
      switchMap((roles) => {
        this.userRoles = roles;
        return of(roles);
      }),
      catchError(() => {
        this.userRoles = [];
        return of([]);
      }),
    );
  }
}
